import useSWR, { type SWRResponse } from 'swr'
import { CommerceError } from './errors'
import type {
  HookSWRInput,
  HookFetchInput,
  HookFetcherOptions,
  HookFetcherFn,
  Fetcher,
  SWRHookSchemaBase,
} from './types'

export type ResponseState<Result> = SWRResponse<Result, CommerceError>

export type UseData = <H extends SWRHookSchemaBase>(
  options: {
    fetchOptions: HookFetcherOptions
    fetcher: HookFetcherFn<H>
  },
  input: HookFetchInput | HookSWRInput,
  fetcherFn: Fetcher
) => ResponseState<H['data']>

const useData: UseData = (options, input, fetcherFn) => {
  const hookInput = Array.isArray(input) ? input : Object.entries(input)
  const fetcher = async (args: any[]) => {
    const [url, query, method, ...rest] = args
    try {
      return await options.fetcher({
        options: { url, query, method },
        // Transform the input array into an object
        input: rest.reduce((obj, val, i) => {
          obj[hookInput[i][0]!] = val
          return obj
        }, {}),
        fetch: fetcherFn,
      })
    } catch (error) {
      // SWR will not log errors, but any error that's not an instance
      // of CommerceError is not welcomed by this hook
      if (!(error instanceof CommerceError)) {
        console.error(error)
      }
      throw error
    }
  }

  const response = useSWR(() => {
    const opts = options.fetchOptions
    return opts
      ? [opts.url, opts.query, opts.method, ...hookInput.map((e) => e[1])]
      : null
  }, fetcher)

  return response as typeof response
}

export default useData
